import { ArrowDown, ArrowUp, PlusCircle, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { NamedLLMConfig } from "@/services/api.service";

import {
  NOTEPAD_MODEL_ROLES,
  gatewayModelIssue,
  modelOptionsFor,
  setNotepadFallbacks,
  setNotepadModel,
  type NotepadModelRole,
} from "./notepadModels";

const GATEWAY_DEFAULT = "__default__";

const IssueBadge = ({ model, role }: { model: string; role: NotepadModelRole }) => {
  const issue = gatewayModelIssue(model, role);
  return issue ? (
    <Badge variant="destructive" className="text-[10px]">
      {issue}
    </Badge>
  ) : null;
};

// A stored value outside the role's options is listed as-is so it survives an untouched save.
const ModelSelect = ({
  role,
  value,
  emptyLabel,
  onChange,
}: {
  role: NotepadModelRole;
  value: string;
  emptyLabel?: string;
  onChange: (model: string) => void;
}) => {
  const options = modelOptionsFor(role);
  return (
    <Select
      value={value || GATEWAY_DEFAULT}
      onValueChange={(v) => onChange(v === GATEWAY_DEFAULT ? "" : v)}
    >
      <SelectTrigger className="h-8 w-full font-mono text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {emptyLabel && <SelectItem value={GATEWAY_DEFAULT}>{emptyLabel}</SelectItem>}
        {value && !options.includes(value) && <SelectItem value={value}>{value}</SelectItem>}
        {options.map((m) => (
          <SelectItem key={m} value={m} className="font-mono text-xs">
            {m}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const NotepadModelsCard = ({
  configs,
  onChange,
  missingGatewayRecord,
}: {
  /** The reserved notepad entries of the TV's llm_configs. */
  configs: NamedLLMConfig[];
  /** Omitted for the read-only view. */
  onChange?: (configs: NamedLLMConfig[]) => void;
  missingGatewayRecord?: boolean;
}) => (
  <Card className="space-y-3 p-4">
    <h4 className="font-medium">
      Notepad Models{" "}
      <span className="text-xs font-normal text-muted-foreground">
        (read by the gateway; fallbacks are tried in order)
      </span>
    </h4>
    {missingGatewayRecord && (
      <p className="text-xs text-destructive">
        No gateway record could be loaded for this tutor version, so the gateway will not see these models.
      </p>
    )}
    <div className="divide-y rounded-lg border">
      {NOTEPAD_MODEL_ROLES.map((role) => {
        const config = configs.find((c) => c.name === role.name)?.config;
        const model = config?.model ?? "";
        const fallbacks = (config?.fallbacks ?? []).map((f) => f.model);
        const setFallbacks = (next: string[]) => onChange?.(setNotepadFallbacks(configs, role.name, next));
        const move = (idx: number, dir: -1 | 1) => {
          const next = [...fallbacks];
          [next[idx], next[idx + dir]] = [next[idx + dir]!, next[idx]!];
          setFallbacks(next);
        };
        const unused = modelOptionsFor(role).find((m) => m !== model && !fallbacks.includes(m));

        if (!onChange) {
          return (
            <div key={role.name} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <span className="w-28 shrink-0 font-medium">{role.label}</span>
              {model ? (
                <span className="break-all font-mono text-xs">{[model, ...fallbacks].join(" → ")}</span>
              ) : (
                <span className="text-muted-foreground">{role.emptyLabel}</span>
              )}
              {[model, ...fallbacks].filter(Boolean).map((m, i) => (
                <IssueBadge key={`${m}-${i}`} model={m} role={role} />
              ))}
            </div>
          );
        }

        return (
          <div key={role.name} className="space-y-2 px-3 py-2">
            <div className="grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-3">
              <span className="text-sm font-medium">{role.label}</span>
              <div className="flex items-center gap-2">
                <ModelSelect
                  role={role}
                  value={model}
                  emptyLabel={role.emptyLabel}
                  onChange={(m) => onChange(setNotepadModel(configs, role.name, m))}
                />
                {model && <IssueBadge model={model} role={role} />}
              </div>
            </div>
            {fallbacks.map((fb, idx) => (
              <div key={idx} className="grid grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-3">
                <span className="text-xs text-muted-foreground">Fallback {idx + 1}</span>
                <div className="flex items-center gap-2">
                  <ModelSelect
                    role={role}
                    value={fb}
                    onChange={(m) => setFallbacks(fallbacks.map((f, i) => (i === idx ? m : f)))}
                  />
                  <IssueBadge model={fb} role={role} />
                </div>
                <div className="flex items-center">
                  <Button
                    type="button" variant="ghost" size="icon" className="h-8 w-8"
                    disabled={idx === 0} onClick={() => move(idx, -1)} title="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-8 w-8"
                    disabled={idx === fallbacks.length - 1} onClick={() => move(idx, 1)} title="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive"
                    onClick={() => setFallbacks(fallbacks.filter((_, i) => i !== idx))}
                    title="Remove fallback"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {model && unused && (
              <Button
                type="button" variant="ghost" size="sm" className="ml-[7.75rem] h-7 text-xs"
                onClick={() => setFallbacks([...fallbacks, unused])}
              >
                <PlusCircle className="mr-1 h-3 w-3" /> Add fallback
              </Button>
            )}
          </div>
        );
      })}
    </div>
  </Card>
);
